import * as React from 'react'
import { useExtendTenantBilling, useSetTenantBillingOwner, useSetTenantComp, useTenantUsers } from '@/hooks/useTenants'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { BILLING_STATE_LABELS, LOCK_REASON_TEXT, billingDateLabel, billingDeadline, dollars } from '@/lib/billing'
import { BILLING_LIVE, MAX_EXTENSION_DAYS } from '@lib/constants/billing'
import type { BillingSummaryDto } from '@lib/dto/billing-summary.dto'
import type { TenantDto } from '@lib/dto/tenant.dto'
import { Callout } from '@/components/ui/callout'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'

/** Add days to a trial or grace period, up to what the 14-day allowance has left. */
function ExtendForm({ tenantId, what, used }: { tenantId: string; what: 'trial' | 'grace'; used: number }) {
  const extend = useExtendTenantBilling(tenantId)
  const left = Math.max(0, MAX_EXTENSION_DAYS - used)
  const [days, setDays] = React.useState(Math.min(7, left))
  // Held to what is left as it shrinks: an extension uses some, and the number
  // typed before it could otherwise ask for more than the allowance has.
  const asked = Math.min(days, left)
  const [error, setError] = React.useState<string | null>(null)
  const noun = what === 'trial' ? 'trial' : 'grace period'

  async function submit(): Promise<void> {
    setError(null)
    try {
      await extend.mutateAsync({ what, days: asked })
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  if (left === 0) {
    return <p className="text-sm text-ink-muted">This {noun} has had all {MAX_EXTENSION_DAYS} extension days.</p>
  }
  return (
    <div className="flex max-w-md items-end gap-2">
      <Field label={`Extend the ${noun} by (days, ${String(left)} left)`} htmlFor={`extend-${what}`} error={error} className="flex-1">
        <Input
          id={`extend-${what}`}
          type="number"
          min={1}
          max={left}
          value={asked}
          onChange={(e) => setDays(Math.max(1, Math.min(left, Math.round(Number(e.target.value) || 1))))}
        />
      </Field>
      <Button pending={extend.isPending} type="button" variant="outline" onClick={() => void submit()}>
        Extend
      </Button>
    </div>
  )
}

/** Comp the company, end its comp, or keep one that is ending. */
function CompControl({ tenantId, billing }: { tenantId: string; billing: BillingSummaryDto }) {
  const setComp = useSetTenantComp(tenantId)
  const [error, setError] = React.useState<string | null>(null)
  // Comping ends a paying organization's subscription, so it is asked first.
  const [confirming, setConfirming] = React.useState(false)

  async function change(comped: boolean): Promise<boolean> {
    setError(null)
    try {
      await setComp.mutateAsync({ comped })
      return true
    } catch (e) {
      setError(requestErrorMessage(e))
      return false
    }
  }

  async function confirmComp(): Promise<void> {
    if (await change(true)) setConfirming(false)
  }

  let action: { label: string; comped: boolean; note: string; ask?: boolean }
  if (billing.state === 'comped' && !BILLING_LIVE) {
    return <p className="text-sm text-ink-muted">Comped. A comp can be ended once billing is live.</p>
  }
  if (billing.state === 'comped') {
    action = {
      label: 'End comp',
      comped: false,
      note: 'Gives 14 days of full access for the billing owner to subscribe; locked after that unless they do.',
    }
  } else if (billing.state === 'comp_ending') {
    action = { label: 'Keep comped', comped: true, note: 'Cancels the notice. The organization stays comped.' }
  } else {
    action = {
      label: 'Comp this organization',
      comped: true,
      note: 'It will never be billed. A paying organization’s subscription is canceled at once, with no refund.',
      ask: true,
    }
  }
  const { comped, ask } = action
  return (
    <div className="space-y-1">
      <Button
        pending={setComp.isPending && !confirming}
        type="button"
        variant="outline"
        onClick={() => {
          if (ask) {
            setError(null)
            setConfirming(true)
          } else void change(comped)
        }}
      >
        {action.label}
      </Button>
      <p className="text-sm text-ink-muted">{action.note}</p>
      {error && !confirming ? <FormError>{error}</FormError> : null}
      <ConfirmDialog
        open={confirming}
        onOpenChange={(next) => {
          if (next || setComp.isPending) return
          setConfirming(false)
          setError(null)
        }}
        title="Comp this organization?"
        description="It will never be billed. If it is paying now, its subscription is canceled at once, with no refund."
        confirmLabel="Comp it"
        destructive
        busy={setComp.isPending}
        error={error}
        onConfirm={() => void confirmComp()}
      />
    </div>
  )
}

/** Where the company stands on billing, and what a super admin can change about it. */
function BillingStatus({ tenant, billing }: { tenant: TenantDto; billing: BillingSummaryDto }) {
  const deadline = billingDeadline(billing)
  const canExtendGrace =
    billing.state === 'past_due' || (billing.state === 'locked' && billing.lockReason === 'non_payment')
  return (
    <Card className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <Badge tone={billing.state === 'locked' || billing.state === 'past_due' ? 'warning' : 'neutral'}>
          {BILLING_STATE_LABELS[billing.state]}
        </Badge>
        {deadline ? (
          <span className="text-ink-muted">
            {deadline.label} {billingDateLabel(deadline.at)}
          </span>
        ) : null}
        <span className="text-ink-muted">{billing.activeVenueCount} active venues</span>
        <span className="text-ink-muted">MRR {dollars(billing.monthlyCents)}</span>
      </div>
      {billing.state === 'locked' ? (
        <p className="text-sm text-ink-muted">{LOCK_REASON_TEXT[billing.lockReason]}</p>
      ) : null}
      <CompControl tenantId={tenant.id} billing={billing} />
      {billing.state === 'trialing' ? <ExtendForm tenantId={tenant.id} what="trial" used={billing.trialExtendedDays} /> : null}
      {canExtendGrace ? <ExtendForm tenantId={tenant.id} what="grace" used={billing.graceExtendedDays} /> : null}
    </Card>
  )
}

/**
 * Who pays for this company, and the super admin's way to choose or move them.
 *
 * Only an active organization admin of the company can hold it, so only they are
 * offered. A company with nobody eligible says so rather than offering an empty
 * list — the fix is to invite an organization admin first.
 */
export function TenantBillingSection({ tenant }: { tenant: TenantDto }) {
  const usersQuery = useTenantUsers(tenant.id)
  const setOwner = useSetTenantBillingOwner(tenant.id)
  const owner = tenant.billingOwner
  const [choice, setChoice] = React.useState(owner?.id ?? '')
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  const eligible = (usersQuery.data?.users ?? []).filter(
    (u) => u.role === 'ORGANIZATION_ADMIN' && u.status === 'ACTIVE',
  )

  async function save(): Promise<void> {
    setError(null)
    setSaved(false)
    try {
      await setOwner.mutateAsync({ userId: choice })
      setSaved(true)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  function renderPicker() {
    if (usersQuery.isPending) return <p className="text-sm text-ink-muted">Loading people…</p>
    if (usersQuery.isError) return <FormError>{requestErrorMessage(usersQuery.error)}</FormError>
    if (eligible.length === 0) {
      return (
        <p className="text-sm text-ink-muted">
          Nobody here can hold billing yet. Invite an organization admin, and once they have accepted they can
          be chosen.
        </p>
      )
    }
    return (
      <div className="flex max-w-md items-end gap-2">
        <Field label="Billing owner" htmlFor="billing-owner" error={error} className="flex-1">
          <Select
            id="billing-owner"
            value={choice}
            onChange={(e) => {
              setChoice(e.target.value)
              setSaved(false)
            }}
          >
            {owner === null ? <option value="">Choose an organization admin…</option> : null}
            {eligible.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </Select>
        </Field>
        <Button pending={setOwner.isPending} type="button" onClick={() => void save()} disabled={choice === '' || choice === owner?.id}>
          {saved ? 'Saved' : 'Save'}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {tenant.billing ? <BillingStatus tenant={tenant} billing={tenant.billing} /> : null}
      {owner === null ? (
        <Callout tone="attention" title="This organization has no billing owner.">
          Billing can’t go live until every organization has one.
        </Callout>
      ) : (
        <p className="text-sm text-ink-muted">
          Billing is managed by <span className="font-medium text-ink">{owner.name}</span> ({owner.email}).
          They can change the card, see invoices and cancel. They can hand it to another organization admin
          themselves; this is for when they cannot.
        </p>
      )}
      {renderPicker()}
    </div>
  )
}
